/**
 * IndexLifecycleMenu — per-row lifecycle action menu for the Indexes tab.
 * Surfaces engine-supported actions (REBUILD / REORGANIZE / REINDEX / SWAP)
 * via a shadcn DropdownMenu when an editable VqeDdlEditContext is supplied.
 * Selecting an action commits an `index-lifecycle` `DdlEdit` via
 * `commitAlterIndex`. mysql renders the trigger disabled with the documented
 * table-level redirect tooltip.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice C5.
 */
import { useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import {
  commitAlterIndex,
  getAlterIndexUnsupportedTooltip,
  getAvailableLifecycleActions,
  getLifecycleActionLabel,
  isAlterIndexSupported,
  type AlterIndexAction,
} from '../../edit/edits/alterIndex';
import type { VqeDdlEditContext } from '../../edit/editContext';
import type { IndexDef } from '../../../../sql-resolver/types/ddlEdit';
import { DisabledAffordance } from './DisabledAffordance';

export interface IndexLifecycleMenuProps {
  readonly editContext: VqeDdlEditContext;
  readonly index: IndexDef;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onError?: (message: string) => void;
}

export function IndexLifecycleMenu(props: IndexLifecycleMenuProps) {
  const { editContext, index, testId, onCommitted, onError } = props;
  const engine = editContext.service.engineId;
  const [busy, setBusy] = useState(false);

  if (!isAlterIndexSupported(engine)) {
    return (
      <DisabledAffordance
        testId={testId}
        tooltip={getAlterIndexUnsupportedTooltip(engine)}
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground"
      >
        <RefreshCw className="h-3.5 w-3.5" />
      </DisabledAffordance>
    );
  }

  const actions = getAvailableLifecycleActions(engine);
  const onSelect = (action: AlterIndexAction): void => {
    if (busy) return;
    setBusy(true);
    void (async () => {
      try {
        await commitAlterIndex(editContext, {
          action,
          indexName: index.name,
          table: index.table,
          // For mssql swap, reuse the row's existing key columns (rebuild
          // in place). Plain-string entries are passed verbatim; expression
          // entries are filtered out — clustered-swap takes column names,
          // not expression text.
          columns: action === 'swap'
            ? index.columns
              .filter((c): c is string => typeof c === 'string')
            : undefined,
          clustered: action === 'swap' ? true : undefined,
        });
        onCommitted?.();
      } catch (err) {
        onError?.(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          aria-label="Index lifecycle actions"
          data-testid={`${testId}-trigger`}
          disabled={busy}
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid={`${testId}-menu`}>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action}
            onSelect={() => onSelect(action)}
            data-testid={`${testId}-action-${action}`}
          >
            {getLifecycleActionLabel(action)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
